import { useToast } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import { ProjectAutocomplete_ProjectFragment } from "src/components/autoPopulateSelects/ProjectAutocompleteField.generated";
import { useTrackedJobs } from "src/contexts/TrackedJobsContext";
import { BillEditor_CommitmentLikeFragment } from "src/fragments/circularFragments.generated";
import { BillType } from "src/generated/graphqlTypes";
import {
  useBillEditor_BillOcrLazyQuery,
  useBillEditor_RunBillParserMutation,
} from "src/routes/bills/BillEditor.generated";
import { BillFormInput } from "../BillEditor";

type Props = {
  formState: ObjectState<BillFormInput>;
  setSelectedCommitments: Dispatch<SetStateAction<BillEditor_CommitmentLikeFragment[]>>;
  setUserHasEditedTotal: Dispatch<SetStateAction<boolean>>;
};

export function useBillOcr(props: Readonly<Props>) {
  const { formState, setSelectedCommitments, setUserHasEditedTotal } = props;
  const [runBillParserMutation] = useBillEditor_RunBillParserMutation();
  const [fetchBillOcr] = useBillEditor_BillOcrLazyQuery();
  const { addJob, dismissTrackedJob } = useTrackedJobs();
  const { showToast } = useToast();
  const [isParsingBill, setIsParsingBill] = useState(false);
  const trackedJobIdRef = useRef<string | undefined>(undefined);

  const runParser = useCallback(async () => {
    const assetId = (formState.documents.value?.[0]?.asset.id || formState.assets?.value?.first?.id) as
      string | undefined;
    if (!assetId) return;

    setIsParsingBill(true);

    try {
      const { data } = await runBillParserMutation({
        variables: { input: { assetId, projectId: formState.project.value?.id } },
      });

      const jobId = data?.runBillParser.trackedJob.id;
      if (!jobId) {
        setIsParsingBill(false);
        return;
      }

      trackedJobIdRef.current = jobId;

      addJob(jobId, "Parsing bill...", {
        onComplete: async (result) => {
          try {
            if (!result) {
              showToast({
                type: "warning",
                message:
                  "No single invoice found in the uploaded document. The document may contain no invoices or multiple invoices. Please fill in the bill details manually.",
              });
              return;
            }
            if (result.__typename !== "BillOcr") return;

            const { data: ocrData } = await fetchBillOcr({ variables: { id: result.id } });
            const billOcr = ocrData?.billOcr;
            if (!billOcr) return;

            // Only fill fields that are currently empty
            if (!formState.tradePartnerNumber.value && billOcr.invoiceNumber) {
              formState.tradePartnerNumber.set(billOcr.invoiceNumber);
            }
            if (!formState.billDate.value && billOcr.billDate) {
              formState.billDate.set(billOcr.billDate);
            }
            if (!formState.dueDate.value && billOcr.dueDate) {
              formState.dueDate.set(billOcr.dueDate);
            }
            if (!formState.proposedAmountInCents.value && billOcr.totalInCents) {
              formState.proposedAmountInCents.set(billOcr.totalInCents);
              setUserHasEditedTotal(true);
            }
            if (!formState.tradePartner.value && billOcr.tradePartner) {
              formState.tradePartner.set(billOcr.tradePartner);
            }
            if (!formState.project.value && billOcr.project) {
              formState.project.set(billOcr.project as ProjectAutocomplete_ProjectFragment);
            }

            if ([BillType.Warranty, BillType.RepairAndMaintenance].includes(billOcr.type.code) && billOcr.project) {
              if (!formState.type.value) formState.type.set(billOcr.type.code);
              if (!formState.billedInCents.value && billOcr.totalInCents) {
                formState.billedInCents.set(billOcr.totalInCents);
              }
            } else if (billOcr.commitmentLikes?.nonEmpty) {
              if (!formState.type.value) formState.type.set(BillType.Standard);
              setSelectedCommitments(billOcr.commitmentLikes as BillEditor_CommitmentLikeFragment[]);
            }
          } finally {
            setIsParsingBill(false);
            trackedJobIdRef.current = undefined;
          }
        },
      });
    } catch (err) {
      console.error(err);
      setIsParsingBill(false);
    }
  }, [
    addJob,
    fetchBillOcr,
    formState,
    runBillParserMutation,
    setSelectedCommitments,
    setUserHasEditedTotal,
    showToast,
  ]);

  return { runParser, isParsingBill, trackedJobIdRef, dismissTrackedJob };
}
